import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Upload, 
  Files, 
  Activity,
  ChevronRight,
  Users
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { clsx } from 'clsx';
import { apiClient } from '../../api/client';

const navigationItems = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    description: 'Overview and recent activity',
  },
  {
    name: 'Upload Files',
    href: '/upload',
    icon: Upload,
    description: 'Upload new files securely',
  },
  {
    name: 'File History',
    href: '/files',
    icon: Files,
    description: 'Browse uploaded files',
  },
];

const adminNavigationItems = [
  {
    name: 'User Management',
    href: '/admin/users',
    icon: Users,
    description: 'Manage users and permissions',
    adminOnly: true,
  },
];

export const Sidebar = () => {
  const { user } = useAuthStore();
  const [quickStats, setQuickStats] = useState<{ filesToday: number; totalUploads: number } | null>(null);

  const allItems = user?.isAdmin 
    ? [...navigationItems, ...adminNavigationItems]
    : navigationItems;

  useEffect(() => {
    const fetchQuickStats = async () => {
      try {
        const response = await apiClient.get('/files/stats/basic');
        if (response.data.success) {
          const data = response.data.data;
          setQuickStats({
            filesToday: data.filesToday || 0,
            totalUploads: data.totalFiles || 0,
          });
        }
      } catch (error) {
        console.error('Failed to fetch quick stats:', error);
        setQuickStats({ filesToday: 0, totalUploads: 0 });
      }
    };

    fetchQuickStats();
  }, []);

  return (
    <aside className="w-64 bg-white border-r border-gray-200 shadow-sm">
      <div className="h-full px-3 py-6">
        <nav className="space-y-2">
          {allItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                clsx(
                  'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                  {
                    'bg-brand-50 text-brand-700 border border-brand-200': isActive,
                    'text-gray-700 hover:bg-gray-50 hover:text-gray-900': !isActive,
                  }
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={clsx(
                      'mr-3 flex-shrink-0 h-5 w-5',
                      {
                        'text-brand-600': isActive,
                        'text-gray-400 group-hover:text-gray-600': !isActive,
                      }
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{item.name}</p>
                    <p className={clsx(
                      'text-xs truncate',
                      {
                        'text-brand-600': isActive,
                        'text-gray-500': !isActive,
                      }
                    )}>
                      {item.description}
                    </p>
                  </div>
                  {isActive && (
                    <ChevronRight className="ml-2 h-4 w-4 text-brand-600" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* System Status */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Activity className="h-4 w-4 text-success-500" />
            <span className="text-sm font-medium text-gray-900">System Status</span>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">API</span>
              <span className="text-success-600 font-medium">Online</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Storage</span>
              <span className="text-success-600 font-medium">Available</span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-4 p-4 bg-brand-50 rounded-lg border border-brand-200">
          <h3 className="text-sm font-medium text-brand-900 mb-2">Quick Stats</h3>
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-brand-700">Files Today</span>
              <span className="text-brand-900 font-medium">
                {quickStats?.filesToday ?? '-'}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-brand-700">Total Uploads</span>
              <span className="text-brand-900 font-medium">
                {quickStats?.totalUploads ?? '-'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};